import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { User, CreditCard, FileText, Camera, MapPin, Building, CheckCircle, XCircle, Search, Eye } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmployeeKYCAdminView() {
  var [employees, setEmployees] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedEmployee, setSelectedEmployee] = useState(null);
  var [kycDetails, setKycDetails] = useState(null);
  var [detailsLoading, setDetailsLoading] = useState(false);
  var [searchTerm, setSearchTerm] = useState('');

  useEffect(function() {
    fetchAllKYC();
  }, []);

  function fetchAllKYC() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/all-kyc')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setEmployees(data.employees || []);
        setLoading(false);
      })
      .catch(function() {
        toast.error('Failed to load KYC data');
        setLoading(false);
      });
  }

  function viewEmployeeKYC(employeeId) {
    setDetailsLoading(true);
    fetch(API_URL + '/api/employee-admin/employee-kyc/' + employeeId)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setKycDetails(data);
        setSelectedEmployee(employeeId);
        setDetailsLoading(false);
      })
      .catch(function() {
        toast.error('Failed to load KYC details');
        setDetailsLoading(false);
      });
  }

  function closeDetails() {
    setSelectedEmployee(null);
    setKycDetails(null);
  }

  var filteredEmployees = employees.filter(function(e) {
    var emp = e.employee;
    var search = searchTerm.toLowerCase();
    return emp.name.toLowerCase().indexOf(search) !== -1 ||
           emp.phone.indexOf(search) !== -1 ||
           (emp.department && emp.department.toLowerCase().indexOf(search) !== -1);
  });

  var employeeRows = filteredEmployees.map(function(item) {
    var emp = item.employee;
    var statusBadges = [];
    
    if (item.has_kyc) {
      if (item.has_bank_details) {
        statusBadges.push(<Badge key="bank" className="bg-green-500/20 text-green-300 mr-1">Bank</Badge>);
      }
      if (item.has_aadhaar) {
        statusBadges.push(<Badge key="aadhaar" className="bg-blue-500/20 text-blue-300 mr-1">Aadhaar</Badge>);
      }
      if (item.has_pan) {
        statusBadges.push(<Badge key="pan" className="bg-purple-500/20 text-purple-300 mr-1">PAN</Badge>);
      }
      if (item.has_selfie) {
        statusBadges.push(<Badge key="selfie" className="bg-amber-500/20 text-amber-300 mr-1">Selfie</Badge>);
      }
    }

    return (
      <div key={emp.id} className="p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors" data-testid={'kyc-row-' + emp.id}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold">
              {emp.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{emp.name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{emp.phone}</span>
                <span>•</span>
                <span>{emp.department || 'N/A'}</span>
                <Badge className={emp.employee_type === 'fleet_manager' ? 'bg-blue-500/20 text-blue-300' : emp.employee_type === 'flex_employee' ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'}>
                  {emp.employee_type.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {statusBadges.length > 0 ? statusBadges : <span className="text-gray-500 text-sm">No KYC</span>}
            </div>
            <Button
              size="sm"
              onClick={function() { viewEmployeeKYC(emp.id); }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid={'view-kyc-' + emp.id}
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>
          </div>
        </div>
      </div>
    );
  });

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Employee KYC & Bank Details
          </CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="pl-9 bg-slate-700/50 border-white/10 text-white"
            />
          </div>
        </div>
        <p className="text-sm text-gray-400">View employee KYC documents, bank details, and profile information</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          </div>
        ) : employees.length > 0 ? (
          <div className="space-y-2">{employeeRows}</div>
        ) : (
          <div className="text-center py-8">
            <User className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400">No employees found</p>
          </div>
        )}
      </CardContent>

      {/* KYC Details Modal */}
      <Dialog open={!!selectedEmployee} onOpenChange={closeDetails}>
        <DialogContent className="bg-slate-800 border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              KYC & Bank Details
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
          ) : kycDetails ? (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-lg font-bold">
                    {kycDetails.employee.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-medium text-lg">{kycDetails.employee.name}</p>
                    <p className="text-gray-400 text-sm">{kycDetails.employee.phone} • {kycDetails.employee.department || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* KYC Status */}
              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: kycDetails.kyc_complete ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)' }}>
                {kycDetails.kyc_complete ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span className={kycDetails.kyc_complete ? 'text-green-400' : 'text-red-400'}>
                  {kycDetails.kyc_complete ? 'KYC Complete' : 'KYC Incomplete'}
                </span>
              </div>

              {/* Bank Details */}
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Building className="w-5 h-5 text-green-400" />
                  <span className="text-white font-medium">Bank Details</span>
                </div>
                {kycDetails.kyc && kycDetails.kyc.bank_account_number ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400">Account Number</p>
                      <p className="text-white font-mono">{kycDetails.kyc.bank_account_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">IFSC Code</p>
                      <p className="text-white font-mono">{kycDetails.kyc.bank_ifsc || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Account Holder</p>
                      <p className="text-white">{kycDetails.kyc.bank_holder_name || kycDetails.employee.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Bank Name</p>
                      <p className="text-white">{kycDetails.kyc.bank_name || 'N/A'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">Bank details not provided</p>
                )}
              </div>

              {/* Aadhaar Details */}
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-medium">Aadhaar Card</span>
                </div>
                {kycDetails.kyc && (kycDetails.kyc.aadhaar_number || kycDetails.kyc.aadhaar_url) ? (
                  <div className="space-y-2">
                    {kycDetails.kyc.aadhaar_number && (
                      <div>
                        <p className="text-xs text-gray-400">Aadhaar Number</p>
                        <p className="text-white font-mono">{kycDetails.kyc.aadhaar_number}</p>
                      </div>
                    )}
                    {kycDetails.kyc.aadhaar_url && (
                      <div>
                        <a href={kycDetails.kyc.aadhaar_url} target="_blank" rel="noopener noreferrer" 
                           className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300">
                          <FileText className="w-4 h-4" />
                          View Aadhaar Document
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400">Aadhaar not provided</p>
                )}
              </div>

              {/* PAN Details */}
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                  <span className="text-white font-medium">PAN Card</span>
                </div>
                {kycDetails.kyc && (kycDetails.kyc.pan_number || kycDetails.kyc.pan_url) ? (
                  <div className="space-y-2">
                    {kycDetails.kyc.pan_number && (
                      <div>
                        <p className="text-xs text-gray-400">PAN Number</p>
                        <p className="text-white font-mono">{kycDetails.kyc.pan_number}</p>
                      </div>
                    )}
                    {kycDetails.kyc.pan_url && (
                      <div>
                        <a href={kycDetails.kyc.pan_url} target="_blank" rel="noopener noreferrer" 
                           className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300">
                          <FileText className="w-4 h-4" />
                          View PAN Document
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400">PAN not provided</p>
                )}
              </div>

              {/* Address */}
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-amber-400" />
                  <span className="text-white font-medium">Address</span>
                </div>
                {kycDetails.kyc && kycDetails.kyc.current_address ? (
                  <p className="text-white">{kycDetails.kyc.current_address}</p>
                ) : (
                  <p className="text-gray-400">Address not provided</p>
                )}
              </div>

              {/* Selfie */}
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Camera className="w-5 h-5 text-pink-400" />
                  <span className="text-white font-medium">Selfie Verification</span>
                </div>
                {kycDetails.kyc && kycDetails.kyc.selfie_url ? (
                  <div className="flex items-center gap-4">
                    <img 
                      src={kycDetails.kyc.selfie_url} 
                      alt="Selfie" 
                      className="w-24 h-24 rounded-lg object-cover border-2 border-white/10"
                    />
                    <a href={kycDetails.kyc.selfie_url} target="_blank" rel="noopener noreferrer" 
                       className="text-pink-400 hover:text-pink-300">
                      View Full Image
                    </a>
                  </div>
                ) : (
                  <p className="text-gray-400">Selfie not provided</p>
                )}
              </div>

              {/* Emergency Contact */}
              {kycDetails.kyc && kycDetails.kyc.emergency_contact && (
                <div className="p-4 bg-slate-700/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-red-400" />
                    <span className="text-white font-medium">Emergency Contact</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400">Name</p>
                      <p className="text-white">{kycDetails.kyc.emergency_contact_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Phone</p>
                      <p className="text-white">{kycDetails.kyc.emergency_contact || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">Failed to load KYC details</p>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
